import {
    Component, ViewEncapsulation, OnInit, OnDestroy, AfterViewInit,
    ViewChild, ElementRef, Renderer2, NgZone, Inject, Input
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { AppContentsService } from '../../../services/app-contents.service';
import { CeTooltipService } from '../../../services/ce-tooltip.service';
import { globals } from '../../../../globals';
import { ISubscription } from 'rxjs/Subscription';
declare var $: any;

@Component({
    selector: 'app-page-contents',
    templateUrl: './page-contents.component.html',
    styleUrls: [
        './page-contents.component.css',
        '../../ui/ce-viewer/ce-viewer.component.css'
    ],
    encapsulation: ViewEncapsulation.None
})

export class PageContentsComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() pageName: string;
    private subscriptions: Array<ISubscription> = [];
    private listeners: Array<any> = [];
    lastUpdate: string;
    title: SafeHtml;
    titleAsContent = false;
    contents: SafeHtml;
    @ViewChild('pageContents') private pageContentsRef: ElementRef;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private renderer: Renderer2,
        private sanitizer: DomSanitizer,
        private router: Router,
        private zone: NgZone,
        private appContentsService: AppContentsService,
        private tooltipService: CeTooltipService
    ) {
        this.tooltipService.setRenderer(this.renderer);
    }

    ngOnInit(): void {
        this.title = globals.html_small_loading_gif;
        this.contents = globals.html_small_loading_gif;

        /* Load page title and contents */
        if (this.pageName) {
            this.subscriptions.push(
                this.appContentsService.getPageContents(this.pageName)
                    .subscribe(
                        page => {
                            if (page && page.name === this.pageName) {
                                this.lastUpdate = page.pubLastDate;

                                /* Assignment of the title with precedence to the title as content */
                                const pageTitle = page.contents.find(c => c.name === 'title');
                                if (pageTitle) {
                                    this.title = this.sanitizer.bypassSecurityTrustHtml(pageTitle.text);
                                    this.titleAsContent = true;
                                } else if (page.title) {
                                    this.title = this.sanitizer.bypassSecurityTrustHtml(page.title);
                                }

                                /* Page contents */
                                const pageDescription = page.contents.find(c => c.name === 'description');
                                if (pageDescription) {
                                    this.contents = this.sanitizer.bypassSecurityTrustHtml(pageDescription.text);
                                } else {
                                    this.contents = '';
                                }
                            }
                        },
                        err => console.error(err)
                    )
            );
        }
    }

    ngAfterViewInit() {
        this.listeners.push(
            this.renderer.listen(this.pageContentsRef.nativeElement, 'click', (event) => { this.onClick(event); }),
            this.renderer.listen(this.pageContentsRef.nativeElement, 'mouseover', (event) => { this.onMouseOver(event); }),
            this.renderer.listen(this.pageContentsRef.nativeElement, 'mouseout', (event) => { this.onMouseLeave(event); })
        );
    }

    onClick(event) {
        event.stopPropagation();
        event.preventDefault();
        let node = event.target;

        /* Citation inside links */
        if ((node.nodeName === 'CITE' || node.nodeName === 'I') && node.parentNode.nodeName === 'A') {
            node = node.parentNode;
        }

        /* Links */
        if (node.nodeName === 'A') {
            const href = node.getAttribute('href');
            if (this.tooltipService.isReferenceLink(href)) {
                /* Show tooltip */
                if (this.tooltipService.showReferenceTooltip(node, href) === false) {
                    this.navigate(href);
                }
            } else if (node.className.indexOf('noteRef') !== -1) { // view the note into apparatus
                const noteId = href.replace('#', '');
                const noteElement = this.pageContentsRef.nativeElement.querySelector('[id="' + noteId + '"]');
                noteElement.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'nearest'});
            } else if (node.className.indexOf('noteNum') !== -1) { // view the note into text
                const noteId = node.getAttribute('id');
                const noteElement = this.pageContentsRef.nativeElement.querySelector('[id="R_' + noteId + '"]');
                noteElement.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'nearest'});
            } else if (node.className.indexOf('tocLink') !== -1) { // Table Of Contexts link
                const tocTargetId = node.getAttribute('href');
                const tocTarget = this.pageContentsRef.nativeElement.querySelector(tocTargetId.replace(/[.]/gi, '\\.')); // manage the '.' in id
                tocTarget.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'nearest'});
            } else if ('collapse' === node.dataset.bsToggle) { // Collapse link
                $(href).slideToggle();
            } else {
                this.navigate(href.replace(/[^#]*[#][/]/g, '')); // remove text before fragment char '#'
            }
        }

        /* Reference links */
        if (node.nodeName === 'DIV') {
            if (node.className.indexOf('ref_link_ms') !== -1) {
                this.zone.run(() => this.router.navigate(['/manuscripts/list/' + node.getAttribute('data-target')]));
            } else if (node.className.indexOf('ref_link_bib') !== -1) {
                this.zone.run(() => this.router.navigate(['/bibliography', {bibId: node.getAttribute('data-target')}]));
            }
        }

        /* Close reference button*/
        if (node.nodeName === 'I' && node.className.indexOf('rtt_close') !== -1) {
            this.tooltipService.removeReferenceTooltip();
        }

    }

    onMouseOver(event) {
        if (event.target.nodeName === 'A' && event.target.className.indexOf('noteRef') !== -1) {
            const href = event.target.getAttribute('href');
            const noteId = href.replace('#', '');
            this.tooltipService.createReferenceTooltip(event.target, this.getNoteText(noteId));
        }
    }

    onMouseLeave(event) {
        if (event.target.nodeName === 'A' && event.target.className.indexOf('noteRef') !== -1) {
            this.tooltipService.removeReferenceTooltip();
        }
    }

    private navigate(href: string) {
        if (href.indexOf('http') === 0) {
            this.document.location.href = href;
        } else {
            this.zone.run(() => this.router.navigate([href]));
        }
    }

    private getNoteText(noteId: string, truncate: number = 500): string {
        let textNode: String = this.pageContentsRef.nativeElement.querySelector('[id="' + noteId + '"]').parentNode.innerText;
        textNode = textNode.slice(noteId.length - 1); // remove note number from text
        if (truncate && truncate < textNode.length) {
            textNode = textNode.substr(0, truncate) + '...';
        }
        return textNode.toString();
    }

    ngOnDestroy() {
        for (const subscription of this.subscriptions) {
            if (subscription) {
                subscription.unsubscribe();
            }
        }
        for (const listener of this.listeners) {
            if (listener) {
                listener();
            }
        }
        this.tooltipService.setRenderer(null);
    }

}



// WEBPACK FOOTER //
// ./src/app/components/ui/page-contents/page-contents.component.ts